import React, { useMemo } from 'react';
import { X, Calendar, Receipt, CreditCard, TrendingUp, Download, Building, Layers, Cpu, Wrench, Package } from 'lucide-react';
import { AdminSubscriptionRecord } from '../../types';
import Button from '../ui/Button';
import { useLocale } from '../../context/LocaleContext';

interface SubscriptionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: AdminSubscriptionRecord | null;
}

interface MonthlyPayment {
    month: string;
    year: number;
    customSolution: number;
    baseCost: number;
    llmUsage: number;
    addOns: number;
    total: number;
    status: 'paid' | 'pending' | 'failed';
}

const SubscriptionDetailsModal: React.FC<SubscriptionDetailsModalProps> = ({
  isOpen,
  onClose,
  subscription
}) => {
  if (!isOpen || !subscription) return null;

  // Mock data generation based on subscription details
  const paymentHistory = useMemo(() => {
    const history: MonthlyPayment[] = [];
    const createdDate = new Date(subscription.createdAt);
    const now = new Date();
    
    // Generate months from creation until now
    let currentDate = new Date(now);
    
    // Ensure we don't go before creation date
    while (currentDate >= createdDate) {
      // Mock random variance for demo purposes but consistent for same component render
      const baseCost = subscription.billing.amount || 99; // Assume current amount is base or derived
      const llmUsage = Math.floor(Math.random() * 50) + 10; // Random LLM usage
      const customSolution = Math.random() > 0.7 ? 150 : 0; // Occasional custom work
      const addOns = Math.random() > 0.8 ? Math.floor(Math.random() * 100) + 20 : 0; // Occasional add-on purchases
      
      history.push({
        month: currentDate.toLocaleString('default', { month: 'long' }),
        year: currentDate.getFullYear(),
        customSolution,
        baseCost,
        llmUsage,
        addOns,
        total: customSolution + baseCost + llmUsage + addOns,
        status: 'paid'
      });
      
      // Go to previous month
      currentDate.setMonth(currentDate.getMonth() - 1);
    }
    
    return history;
  }, [subscription]);

  const totalRevenue = paymentHistory.reduce((sum, item) => sum + item.total, 0);

  const { formatCurrency: formatLocaleCurrency, formatDate } = useLocale();

  const formatCurrency = (amount: number) => {
    return formatLocaleCurrency(amount);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm h-screen">
      <div className="bg-white dark:bg-surface w-full max-w-4xl rounded-xl shadow-2xl flex flex-col h-[90vh] border dark:border-border-color border-light-border-color animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="p-6 border-b dark:border-border-color border-light-border-color flex justify-between items-start">
          <div>
             <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-primary/10 rounded-lg">
                    <Receipt className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold dark:text-white text-gray-900">
                    Payment History
                </h2>
             </div>
            <p className="dark:text-text-secondary text-gray-500 flex items-center gap-2">
                <Building className="w-4 h-4" />
                {subscription.tenantName} 
                <span className="mx-2">•</span>
                <span className="font-mono text-xs opacity-75">{subscription.subscriptionId}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
            
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-primary/5 dark:bg-primary/10 p-4 rounded-xl border border-primary/10">
                    <p className="text-sm text-primary font-medium mb-1">Total Revenue</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(totalRevenue)}</p>
                    <p className="text-xs text-primary/70 mt-1">All time revenue</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border dark:border-gray-700 border-gray-200">
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-1">Total Invoices</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{paymentHistory.length}</p>
                    <p className="text-xs text-gray-400 mt-1">Generated invoices</p>
                </div>
                 <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border dark:border-gray-700 border-gray-200">
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-1">Average Monthly</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(totalRevenue / (paymentHistory.length || 1))}</p>
                     <p className="text-xs text-gray-400 mt-1">Avg. revenue per month</p>
                </div>
                 <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border dark:border-gray-700 border-gray-200">
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-1">Current Plan</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white truncate">{subscription.planName || subscription.planId}</p>
                     <p className="text-xs text-gray-400 mt-1 capitalize">{subscription.status}</p>
                </div>
            </div>

            {/* History Table */}
            <div className="bg-white dark:bg-surface border dark:border-border-color border-light-border-color rounded-xl overflow-hidden shadow-sm">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 dark:bg-gray-800/50 border-b dark:border-border-color border-light-border-color">
                        <tr>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Month</th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">
                                <div className="flex items-center justify-end gap-1">
                                    <Wrench className="w-3 h-3" />
                                    Custom Solution
                                </div>
                            </th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">
                                <div className="flex items-center justify-end gap-1">
                                    <Layers className="w-3 h-3" />
                                    Base Cost
                                </div>
                            </th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">
                                <div className="flex items-center justify-end gap-1">
                                    <Cpu className="w-3 h-3" />
                                    LLM Usage
                                </div>
                            </th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">
                                <div className="flex items-center justify-end gap-1">
                                    <Package className="w-3 h-3" />
                                    Add-ons
                                </div>
                            </th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right font-bold text-primary">Total</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-gray-800 divide-gray-100">
                        {paymentHistory.map((payment, index) => (
                            <tr key={`${payment.year}-${payment.month}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                                <td className="px-6 py-4 dark:text-gray-300 text-gray-700">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium">{payment.month} {payment.year}</span>
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-right dark:text-gray-400 text-gray-600">
                                    {payment.customSolution > 0 ? (
                                        <span className="text-secondary-500 font-medium">{formatCurrency(payment.customSolution)}</span>
                                    ) : (
                                        <span className="opacity-30">—</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-right dark:text-gray-400 text-gray-600">
                                    {formatCurrency(payment.baseCost)}
                                </td>
                                <td className="px-6 py-4 text-right dark:text-gray-400 text-gray-600">
                                    {formatCurrency(payment.llmUsage)}
                                </td>
                                <td className="px-6 py-4 text-right dark:text-gray-400 text-gray-600">
                                    {payment.addOns > 0 ? (
                                        <span className="text-primary font-medium">{formatCurrency(payment.addOns)}</span>
                                    ) : (
                                        <span className="opacity-30">—</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-right font-bold dark:text-white text-gray-900">
                                    {formatCurrency(payment.total)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot className="bg-primary/5 dark:bg-primary/10 font-bold border-t border-primary/20">
                         <tr>
                            <td className="px-6 py-4 text-primary">All Time Totals</td>
                             <td className="px-6 py-4 text-right text-primary">
                                {formatCurrency(paymentHistory.reduce((acc, p) => acc + p.customSolution, 0))}
                             </td>
                             <td className="px-6 py-4 text-right text-primary">
                                {formatCurrency(paymentHistory.reduce((acc, p) => acc + p.baseCost, 0))}
                             </td>
                             <td className="px-6 py-4 text-right text-primary">
                                {formatCurrency(paymentHistory.reduce((acc, p) => acc + p.llmUsage, 0))}
                             </td>
                             <td className="px-6 py-4 text-right text-primary">
                                {formatCurrency(paymentHistory.reduce((acc, p) => acc + p.addOns, 0))}
                             </td>
                             <td className="px-6 py-4 text-right text-primary text-lg">
                                {formatCurrency(totalRevenue)}
                             </td>
                         </tr>
                    </tfoot>
                </table>
            </div>

        </div>

        {/* Footer */}
        <div className="p-6 border-t dark:border-border-color border-light-border-color flex justify-end gap-3 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button disabled title="Export feature coming soon">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDetailsModal;
